import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional second line (e.g. email, code) used by the search and shown muted */
  sublabel?: string;
}

/**
 * Searchable multi-select dropdown. Used by audit/access-log filters and
 * everywhere else a user picks "any of these users / any of these actions".
 *
 * - Selected values appear as removable chips inside the control.
 * - Typing filters the open menu by case-insensitive substring on label + sublabel.
 * - Click-outside closes the menu.
 * - `value` is controlled (array of option `value`s).
 */
export function MultiSelectSearch({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  id,
  className,
  maxChips = 3,
}: {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  maxChips?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(o.value)),
    [options, selectedSet],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  function toggle(v: string) {
    if (selectedSet.has(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  }

  function removeOne(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  function clearAll() {
    onChange([]);
    setQuery('');
  }

  // Limit the visible chips inside the control; the rest go behind a "+N" pill.
  const visibleChips = selectedOptions.slice(0, maxChips);
  const overflow = selectedOptions.length - visibleChips.length;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div
        id={id}
        role="combobox"
        aria-expanded={open}
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className={cn(
          'input flex items-center gap-1 h-10 cursor-pointer overflow-hidden pr-8',
          selectedOptions.length === 0 && 'text-ink-muted',
        )}
      >
        {selectedOptions.length === 0 && <span className="text-sm">{placeholder}</span>}
        {visibleChips.map((o) => (
          <span
            key={o.value}
            className="inline-flex items-center gap-1 rounded bg-navy-50 text-navy text-[11px] font-medium px-1.5 py-0.5 max-w-[140px]"
          >
            <span className="truncate">{o.label}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeOne(o.value); }}
              className="hover:text-danger-700"
              aria-label={`Remove ${o.label}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {overflow > 0 && (
          <span className="inline-flex items-center rounded bg-navy-100 text-navy text-[11px] font-medium px-1.5 py-0.5">
            +{overflow}
          </span>
        )}
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded border border-surface-border bg-white shadow-card-3 overflow-hidden">
          <div className="flex items-center gap-2 border-b border-surface-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-ink-muted" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="flex-1 bg-transparent text-xs outline-none"
            />
            {(query || value.length > 0) && (
              <button
                type="button"
                onClick={clearAll}
                className="text-[10px] text-ink-muted hover:text-danger-700"
              >
                Clear
              </button>
            )}
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-muted">No matches.</li>
            )}
            {filtered.map((o) => {
              const checked = selectedSet.has(o.value);
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => toggle(o.value)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-muted',
                      checked && 'bg-navy-50',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 h-3.5 w-3.5 shrink-0 rounded border grid place-items-center',
                        checked ? 'border-navy bg-navy text-white' : 'border-surface-border',
                      )}
                    >
                      {checked && <Check className="h-2.5 w-2.5" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink-primary">{o.label}</span>
                      {o.sublabel && (
                        <span className="block truncate text-[10px] text-ink-muted">{o.sublabel}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
